import { useId, useLayoutEffect, useState } from "react";
import { useTimelineCtx } from "../ctx";

interface IProps {
  perRow: number;
  totalItems: number;
  containerRef: React.RefObject<HTMLElement | null>;
  cornerRadius?: number;
  bulletAtBottom?: boolean;
}

interface IBulletPos {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const TimelineSnakeLine = ({
  perRow,
  totalItems,
  containerRef,
  cornerRadius = 40,
  bulletAtBottom: _bulletAtBottom = false,
}: IProps) => {
  const { markerSizes } = useTimelineCtx();
  const [size, setSize] = useState({ width: 0, height: 0 });
  // One entry per item (in source order) — the bullet's center + box
  // measured from the snake grid container.
  const [bullets, setBullets] = useState<IBulletPos[]>([]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
      const markers = el.querySelectorAll<HTMLElement>(".timeline-item-marker");
      const elTop = rect.top;
      const elLeft = rect.left;
      const next: IBulletPos[] = [];
      markers.forEach((m) => {
        const r = m.getBoundingClientRect();
        next.push({
          x: r.left - elLeft + r.width / 2,
          y: r.top - elTop + r.height / 2,
          w: r.width,
          h: r.height,
        });
      });
      setBullets(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // markerSizes triggers re-measure once each per-item track-segment
    // registers — that's the signal that bullets are in their final
    // positions (auto grid rows don't stabilize until then).
  }, [containerRef, perRow, totalItems, markerSizes]);

  const maskId = `timeline-snake-cuts-${useId()}`;

  if (size.width === 0 || bullets.length < totalItems) return null;

  const rowCount = Math.ceil(totalItems / perRow);
  const gridWidth = size.width;
  const edgeInset = 5;

  // Group bullets by row in source order. perRow gives the row size; the
  // bullet's traversal direction (LTR / RTL) per row is implicit in the
  // source-order traversal we already use for content layout.
  const rowBullets: IBulletPos[][] = [];
  for (let i = 0; i < totalItems; i++) {
    const rowIdx = Math.floor(i / perRow);
    if (!rowBullets[rowIdx]) rowBullets[rowIdx] = [];
    rowBullets[rowIdx].push(bullets[i]);
  }

  let d = "";
  for (let r = 0; r < rowCount; r++) {
    const evenRow = r % 2 === 0;
    const isFirstRow = r === 0;
    const isLastRow = r === rowCount - 1;
    const items = rowBullets[r] ?? [];
    if (items.length === 0) continue;
    // The path follows the bullets' actual y for this row; if any item's
    // y differs (it shouldn't in a uniform grid row), the line still
    // tracks the first item's bullet center.
    const y = items[0].y;
    const dir = evenRow ? 1 : -1;
    const rowEndX = evenRow ? gridWidth - edgeInset : edgeInset;

    if (isFirstRow) {
      d += `M ${items[0].x} ${y} `;
    }

    // For the last row, terminate at the actual last item's x (handles
    // partial rows where fewer than perRow items remain). For other rows,
    // line continues to the bend.
    const lastItemInRow = items[items.length - 1];
    const lineEndX = isLastRow ? lastItemInRow.x : rowEndX - dir * cornerRadius;
    d += `L ${lineEndX} ${y} `;

    if (!isLastRow) {
      const nextItems = rowBullets[r + 1];
      if (!nextItems || nextItems.length === 0) break;
      const nextY = nextItems[0].y;
      d += `Q ${rowEndX} ${y} ${rowEndX} ${y + cornerRadius} `;
      d += `L ${rowEndX} ${nextY - cornerRadius} `;
      d += `Q ${rowEndX} ${nextY} ${rowEndX - dir * cornerRadius} ${nextY} `;
    }
  }

  // Cut a gap around every bullet so the line breaks visually under each
  // marker. Gap is sized to each marker's measured bounds + breathing
  // room; min radius guarantees a visible cut for tiny / empty markers.
  const minCutRadius = 24;
  const cutPaddingX = 16;
  const cutPaddingY = 8;
  const cuts = bullets.map((b, i) => {
    const m = markerSizes.get(i);
    const halfW = m ? m.width / 2 : b.w / 2;
    const halfH = m ? m.height / 2 : b.h / 2;
    return {
      cx: b.x,
      cy: b.y,
      rx: Math.max(halfW + cutPaddingX, minCutRadius),
      ry: Math.max(halfH + cutPaddingY, minCutRadius),
    };
  });

  return (
    <svg
      className="tw-pointer-events-none tw-absolute"
      style={{
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        overflow: "visible",
      }}
      // 100% sizing + viewBox = measured grid + preserveAspectRatio="none"
      // lets the path track the bullets visually even if size.width /
      // size.height were captured during a transient layout (zoom, reflow).
      width="100%"
      height="100%"
      viewBox={`0 0 ${size.width} ${size.height}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      overflow="visible"
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={size.width}
          height={size.height}
        >
          <rect
            x="0"
            y="0"
            width={size.width}
            height={size.height}
            fill="white"
          />
          {cuts.map((c, i) => (
            <ellipse
              key={i}
              cx={c.cx}
              cy={c.cy}
              rx={c.rx}
              ry={c.ry}
              fill="black"
            />
          ))}
        </mask>
      </defs>
      <path
        d={d.trim()}
        stroke="var(--timeline-color)"
        strokeWidth={4}
        // Keeps the stroke uniform regardless of the non-uniform SVG
        // stretch from preserveAspectRatio="none".
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
};
